import React, { useState, useEffect, useRef } from 'react';

const colors = {
    deepBlue: '#1A3C8F',
    blue: '#185FA5',
    lightBlue: '#378ADD',
    orange: '#F57C00',
    green: '#4CAF50',
    white: '#ffffff',
};

const emptyItem = () => ({
    id: Date.now() + Math.random(),
    productId: '',
    productName: '',
    category: '',
    variantId: '',
    variantName: '',
    quantity: '',
    unit: 'pcs',
    specifications: '',
});

// ── Product Search Dropdown ──
const ProductDropdown = ({ itemId, productName, products, onProductSelect }) => {
    const [search, setSearch] = useState(productName || '');
    const [open, setOpen] = useState(false);
    const ref = useRef();

    const prevProductName = useRef(productName);
    useEffect(() => {
        if (prevProductName.current !== productName) {
            setSearch(productName || '');
            prevProductName.current = productName;
        }
    }, [productName]);

    const filtered = products.filter(p =>
        p.productName.toLowerCase().includes(search.toLowerCase())
    );

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (product) => {
        setSearch(product.productName);
        setOpen(false);
        onProductSelect(product);
    };

    return (
        <div ref={ref} style={{ position: 'relative', width: '100%' }}>
            <input
                type="text"
                placeholder="Search product..."
                value={search}
                onChange={e => { setSearch(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                autoComplete="off"
                style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    border: `1.5px solid ${open ? colors.lightBlue : '#B5D4F4'}`,
                    fontSize: '14px',
                    outline: 'none',
                    background: '#FAFCFF',
                    color: '#2d2d2d',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                }}
            />
            {open && filtered.length > 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%', left: 0, right: 0,
                    background: '#fff',
                    border: `1.5px solid #B5D4F4`,
                    borderRadius: '8px',
                    zIndex: 999,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    boxShadow: '0 8px 24px rgba(26,60,143,0.12)',
                    marginTop: '4px',
                }}>
                    {filtered.map(p => (
                        <div
                            key={p.id}
                            onMouseDown={() => handleSelect(p)}
                            style={{
                                padding: '10px 14px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #EEF4FB',
                                fontSize: '13px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#EEF4FB'}
                            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                        >
                            <span style={{ fontWeight: 600, color: colors.deepBlue }}>{p.productName}</span>
                            <span style={{ fontSize: '11px', color: '#888' }}>{p.Category?.name || ''}</span>
                        </div>
                    ))}
                </div>
            )}
            {open && search && filtered.length === 0 && (
                <div style={{
                    position: 'absolute',
                    top: '100%', left: 0, right: 0,
                    background: '#fff',
                    border: `1.5px solid #B5D4F4`,
                    borderRadius: '8px',
                    zIndex: 999,
                    padding: '12px 14px',
                    fontSize: '13px',
                    color: '#999',
                    marginTop: '4px',
                }}>
                    No products found
                </div>
            )}
        </div>
    );
};

// ── Variant Dropdown ──
const VariantDropdown = ({ item, variants, onVariantSelect }) => {
    if (!item.productId || variants.length === 0) {
        return (
            <select disabled style={{
                width: '100%', padding: '10px 8px', borderRadius: '8px',
                border: '1.5px solid #e0e0e0', fontSize: '14px', background: '#f5f5f5',
                color: '#aaa', cursor: 'not-allowed', boxSizing: 'border-box',
            }}>
                <option>Select product first</option>
            </select>
        );
    }

    return (
        <select
            value={item.variantId}
            onChange={e => {
                const v = variants.find(v => String(v.id) === e.target.value);
                onVariantSelect(v);
            }}
            style={{
                width: '100%', padding: '10px 8px', borderRadius: '8px',
                border: `1.5px solid #B5D4F4`, fontSize: '14px',
                background: '#FAFCFF', color: '#2d2d2d', boxSizing: 'border-box',
                outline: 'none',
            }}
        >
            <option value="">Select variant</option>
            {variants.map(v => (
                <option key={v.id} value={v.id}>
                    {v.variantName} {v.unit ? `(${v.unit})` : ''} — ₹{v.salesPrice}
                </option>
            ))}
        </select>
    );
};

const BulkOrderForm = () => {
    const [customerInfo, setCustomerInfo] = useState({
        name: '', email: '', phone: '', company: '',
        deliveryAddress: '', deliveryDate: '', notes: '',
    });

    const [orderItems, setOrderItems] = useState([emptyItem()]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [products, setProducts] = useState([]);
    const [variantsMap, setVariantsMap] = useState({});
    const [loadingProducts, setLoadingProducts] = useState(true);

    useEffect(() => {
        const apiUrl = process.env.REACT_APP_API_URL;
        fetch(`${apiUrl}/products`)
            .then(res => res.json())
            .then(data => {
                setProducts(Array.isArray(data) ? data : []);
                const map = {};
                (Array.isArray(data) ? data : []).forEach(p => {
                    if (p.Variants && p.Variants.length > 0) map[p.id] = p.Variants;
                });
                setVariantsMap(map);
            })
            .catch(err => console.error('Failed to fetch products:', err))
            .finally(() => setLoadingProducts(false));
    }, []);

    const handleProductSelect = (itemId, product) => {
        setOrderItems(prev => prev.map(item =>
            item.id === itemId
                ? { ...item, productId: product.id, productName: product.productName, category: product.Category?.name || '', variantId: '', variantName: '', unit: 'pcs', specifications: '' }
                : item
        ));
    };

    const handleVariantSelect = (itemId, variant) => {
        if (!variant) return;
        setOrderItems(prev => prev.map(item =>
            item.id === itemId
                ? { ...item, variantId: variant.id, variantName: variant.variantName, unit: variant.unit || item.unit, specifications: `MRP: ₹${variant.mrp} | Price: ₹${variant.salesPrice}` }
                : item
        ));
    };

    const handleItemChange = (id, field, value) => {
        setOrderItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    };

    const handleCustomerChange = (e) => {
        setCustomerInfo(prev => ({ ...prev, [e.target.id]: e.target.value }));
    };

    const addItem = () => setOrderItems(prev => [...prev, emptyItem()]);

    const removeItem = (id) => {
        if (orderItems.length === 1) return;
        setOrderItems(prev => prev.filter(item => item.id !== id));
    };

    const duplicateItem = (id) => {
        const item = orderItems.find(i => i.id === id);
        const newItem = { ...item, id: Date.now() + Math.random() };
        const idx = orderItems.findIndex(i => i.id === id);
        const updated = [...orderItems];
        updated.splice(idx + 1, 0, newItem);
        setOrderItems(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const itemsValid = orderItems.every(i => i.productName && i.quantity);
        if (!itemsValid) {
            alert('Product Name and Quantity are required for every item.');
            return;
        }

        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
            alert('API URL is not configured. Check REACT_APP_API_URL in your .env file.');
            return;
        }

        setSubmitting(true);

        try {
            // Map bulk form fields → existing /orders/save endpoint shape
            const payload = {
                customer_name:    customerInfo.name,
                customer_email:   customerInfo.email,
                customer_phone:   customerInfo.phone,
                billing_address:  customerInfo.deliveryAddress,
                shipping_address: customerInfo.deliveryAddress,
                order_notes:      [
                    customerInfo.company ? `Company: ${customerInfo.company}` : '',
                    customerInfo.deliveryDate ? `Expected Delivery: ${customerInfo.deliveryDate}` : '',
                    customerInfo.notes || '',
                ].filter(Boolean).join(' | '),
                subtotal:         0,
                tax:              0,
                discount:         0,
                total:            0,
                payment_id:       null,
                razorpay_order_id: null,
                items: orderItems.map(i => ({
                    product_id:  i.productId  || null,
                    variant_id:  i.variantId  || null,
                    name:        i.productName,
                    variantName: i.variantName || null,
                    quantity:    parseInt(i.quantity) || 1,
                    price:       0,
                    unit:        i.unit        || 'pcs',
                    weight:      null,
                    image:       null,
                })),
            };

            const response = await fetch(`${apiUrl}/orders/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            // Catch HTTP-level errors (404, 500, etc.) before parsing JSON
            if (!response.ok) {
                const errText = await response.text();
                throw new Error(`Server error ${response.status}: ${errText}`);
            }

            const result = await response.json();

            if (result.success) {
                setSubmitted(true);
                setCustomerInfo({
                    name: '', email: '', phone: '', company: '',
                    deliveryAddress: '', deliveryDate: '', notes: '',
                });
                setOrderItems([emptyItem()]);
            } else {
                alert(`Submission failed: ${result.message || 'Unknown error from server.'}`);
            }
        } catch (error) {
            console.error('Submit error:', error);
            alert(`An error occurred: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const cardStyle = {
        border: '1.5px solid #B5D4F4',
        boxShadow: '0 4px 24px rgba(26,60,143,0.08)',
        borderRadius: '16px', padding: '32px 28px',
        background: colors.white, marginBottom: '24px',
    };

    const labelStyle = {
        fontSize: '13px', fontWeight: '600', color: '#1A3C8F',
        marginBottom: '6px', display: 'block',
    };

    const inputStyle = {
        width: '100%', padding: '10px 14px', borderRadius: '8px',
        border: `1.5px solid #B5D4F4`,
        fontSize: '14px', outline: 'none', transition: 'border-color 0.2s',
        background: '#FAFCFF', color: '#2d2d2d', boxSizing: 'border-box',
    };

    if (submitted) {
        return (
            <section className="contact py-80">
                <div className="container container-lg">
                    <div style={{ ...cardStyle, textAlign: 'center', padding: '60px 40px' }}>
                        <div style={{ width: '72px', height: '72px', borderRadius: '50%', background: colors.green, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
                            <i className="ph-fill ph-check" style={{ fontSize: '36px', color: '#fff' }} />
                        </div>
                        <h4 style={{ color: colors.deepBlue, fontWeight: 700, marginBottom: '12px' }}>Order Submitted Successfully!</h4>
                        <p style={{ color: '#666', marginBottom: '28px', fontSize: '15px' }}>Your bulk order has been received. We will contact you shortly.</p>
                        <button onClick={() => setSubmitted(false)} style={{ background: colors.deepBlue, color: '#fff', border: 'none', padding: '12px 32px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>
                            New Order
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="contact py-80">
            <div className="container container-lg">

                <div style={{ marginBottom: '32px' }}>
                    <h4 style={{ color: colors.deepBlue, fontWeight: 700, marginBottom: '6px' }}>Bulk Order Request</h4>
                    <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>Add multiple products in a single order. Include all required items below.</p>
                </div>

                {loadingProducts && (
                    <div style={{ background: '#EEF4FB', borderRadius: '10px', padding: '12px 20px', marginBottom: '20px', color: colors.blue, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <i className="ph ph-spinner" /> Loading products...
                    </div>
                )}

                <form onSubmit={handleSubmit}>

                    {/* Customer Info */}
                    <div style={cardStyle}>
                        <h6 style={{ color: colors.deepBlue, fontWeight: 700, marginBottom: '24px', fontSize: '15px' }}>
                            <i className="ph-fill ph-user-circle" style={{ marginRight: '8px', color: colors.orange }} />
                            Customer Details
                        </h6>
                        <div className="row">
                            {[
                                { id: 'name',         label: 'Full Name',                 placeholder: 'Your full name',        type: 'text',  required: true  },
                                { id: 'email',        label: 'Email Address',              placeholder: 'email@example.com',     type: 'email', required: true  },
                                { id: 'phone',        label: 'Phone Number',               placeholder: '+91 XXXXX XXXXX',       type: 'tel',   required: true  },
                                { id: 'company',      label: 'Company / Business Name',    placeholder: 'Company name (optional)', type: 'text', required: false },
                                { id: 'deliveryDate', label: 'Expected Delivery Date',     placeholder: '',                      type: 'date',  required: false },
                            ].map(f => (
                                <div key={f.id} className="col-sm-6" style={{ marginBottom: '16px' }}>
                                    <label htmlFor={f.id} style={labelStyle}>
                                        {f.label} {f.required && <span style={{ color: colors.orange }}>*</span>}
                                    </label>
                                    <input
                                        type={f.type} id={f.id} placeholder={f.placeholder}
                                        value={customerInfo[f.id]} onChange={handleCustomerChange}
                                        required={f.required} autoComplete="off"
                                        style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = colors.lightBlue}
                                        onBlur={e => e.target.style.borderColor = '#B5D4F4'}
                                    />
                                </div>
                            ))}
                            <div className="col-sm-12" style={{ marginBottom: '16px' }}>
                                <label htmlFor="deliveryAddress" style={labelStyle}>
                                    Delivery Address <span style={{ color: colors.orange }}>*</span>
                                </label>
                                <textarea
                                    id="deliveryAddress" placeholder="Enter full delivery address..."
                                    rows={3} value={customerInfo.deliveryAddress}
                                    onChange={handleCustomerChange} required
                                    style={{ ...inputStyle, resize: 'vertical' }}
                                    onFocus={e => e.target.style.borderColor = colors.lightBlue}
                                    onBlur={e => e.target.style.borderColor = '#B5D4F4'}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Order Items */}
                    <div style={cardStyle}>
                        <div style={{ marginBottom: '24px' }}>
                            <h6 style={{ color: colors.deepBlue, fontWeight: 700, fontSize: '15px', margin: 0 }}>
                                <i className="ph-fill ph-package" style={{ marginRight: '8px', color: colors.orange }} />
                                Order Items
                                <span style={{ marginLeft: '10px', background: colors.orange, color: '#fff', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: '700' }}>
                                    {orderItems.length} item{orderItems.length > 1 ? 's' : ''}
                                </span>
                            </h6>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 80px 90px', gap: '10px', padding: '8px 12px', background: '#EEF4FB', borderRadius: '8px', marginBottom: '12px', fontSize: '12px', fontWeight: '700', color: colors.deepBlue }} className="d-none d-md-grid">
                            <span>Product *</span>
                            <span>Variant / Size</span>
                            <span>Category</span>
                            <span>Qty *</span>
                            <span>Actions</span>
                        </div>

                        {orderItems.map((item, index) => {
                            const itemVariants = variantsMap[item.productId] || [];
                            return (
                                <div key={item.id} style={{ border: '1.5px solid #e0ecf8', borderRadius: '10px', padding: '14px 12px', marginBottom: '10px', background: index % 2 === 0 ? '#FAFCFF' : '#F5F9FF', position: 'relative' }}>
                                    <span style={{ position: 'absolute', top: '-10px', left: '12px', background: colors.deepBlue, color: '#fff', borderRadius: '20px', padding: '1px 10px', fontSize: '11px', fontWeight: '700' }}>#{index + 1}</span>

                                    {/* Desktop */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr 1fr 80px 90px', gap: '10px', alignItems: 'center' }} className="d-none d-md-grid">
                                        <ProductDropdown
                                            itemId={item.id}
                                            productName={item.productName}
                                            products={products}
                                            onProductSelect={(p) => handleProductSelect(item.id, p)}
                                        />
                                        <VariantDropdown item={item} variants={itemVariants} onVariantSelect={(v) => handleVariantSelect(item.id, v)} />
                                        <input type="text" value={item.category} readOnly style={{ ...inputStyle, background: '#f0f4fa', color: '#666', cursor: 'default' }} />
                                        <input type="number" placeholder="Qty" min="1" value={item.quantity} onChange={e => handleItemChange(item.id, 'quantity', e.target.value)} required style={inputStyle} />
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button type="button" title="Duplicate" onClick={() => duplicateItem(item.id)} style={{ background: '#EEF4FB', border: 'none', borderRadius: '6px', padding: '8px 10px', cursor: 'pointer', color: colors.blue, fontSize: '15px', lineHeight: 1 }}>
                                                <i className="ph ph-copy" />
                                            </button>
                                            <button type="button" title="Remove" onClick={() => removeItem(item.id)} disabled={orderItems.length === 1} style={{ background: orderItems.length === 1 ? '#f0f0f0' : '#FFF0F0', border: 'none', borderRadius: '6px', padding: '8px 10px', cursor: orderItems.length === 1 ? 'not-allowed' : 'pointer', color: orderItems.length === 1 ? '#aaa' : '#E53935', fontSize: '15px', lineHeight: 1 }}>
                                                <i className="ph ph-trash" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Mobile */}
                                    <div className="d-md-none" style={{ paddingTop: '8px' }}>
                                        <div className="row gy-2">
                                            <div className="col-12">
                                                <label style={{ ...labelStyle, fontSize: '12px' }}>Product *</label>
                                                <ProductDropdown itemId={item.id} productName={item.productName} products={products} onProductSelect={(p) => handleProductSelect(item.id, p)} />
                                            </div>
                                            <div className="col-12">
                                                <label style={{ ...labelStyle, fontSize: '12px' }}>Variant / Size</label>
                                                <VariantDropdown item={item} variants={itemVariants} onVariantSelect={(v) => handleVariantSelect(item.id, v)} />
                                            </div>
                                            <div className="col-6">
                                                <label style={{ ...labelStyle, fontSize: '12px' }}>Category</label>
                                                <input type="text" value={item.category} readOnly style={{ ...inputStyle, background: '#f0f4fa', color: '#666' }} />
                                            </div>
                                            <div className="col-6">
                                                <label style={{ ...labelStyle, fontSize: '12px' }}>Qty *</label>
                                                <input type="number" placeholder="0" min="1" value={item.quantity} onChange={e => handleItemChange(item.id, 'quantity', e.target.value)} required style={inputStyle} />
                                            </div>
                                            <div className="col-12" style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button type="button" onClick={() => duplicateItem(item.id)} style={{ background: '#EEF4FB', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: 'pointer', color: colors.blue, fontSize: '13px' }}>
                                                    <i className="ph ph-copy" /> Copy
                                                </button>
                                                <button type="button" onClick={() => removeItem(item.id)} disabled={orderItems.length === 1} style={{ background: orderItems.length === 1 ? '#f0f0f0' : '#FFF0F0', border: 'none', borderRadius: '6px', padding: '8px 14px', cursor: orderItems.length === 1 ? 'not-allowed' : 'pointer', color: orderItems.length === 1 ? '#aaa' : '#E53935', fontSize: '13px' }}>
                                                    <i className="ph ph-trash" /> Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <button type="button" onClick={addItem}
                            style={{ width: '100%', padding: '12px', borderRadius: '10px', border: `2px dashed #B5D4F4`, background: 'transparent', color: colors.blue, fontWeight: '600', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '4px', transition: 'all 0.2s' }}
                            onMouseOver={e => { e.currentTarget.style.background = '#EEF4FB'; e.currentTarget.style.borderColor = colors.lightBlue; }}
                            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#B5D4F4'; }}
                        >
                            <i className="ph ph-plus-circle" style={{ fontSize: '18px' }} /> Add Another Item
                        </button>
                    </div>

                    {/* Notes */}
                    <div style={cardStyle}>
                        <h6 style={{ color: colors.deepBlue, fontWeight: 700, marginBottom: '16px', fontSize: '15px' }}>
                            <i className="ph-fill ph-note-pencil" style={{ marginRight: '8px', color: colors.orange }} />
                            Additional Notes
                        </h6>
                        <textarea
                            id="notes" placeholder="Special instructions, packaging requirements, custom requests..."
                            rows={4} value={customerInfo.notes} onChange={handleCustomerChange}
                            style={{ ...inputStyle, width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                            onFocus={e => e.target.style.borderColor = colors.lightBlue}
                            onBlur={e => e.target.style.borderColor = '#B5D4F4'}
                        />
                    </div>

                    {/* Summary Bar */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px', background: '#EEF4FB', borderRadius: '12px', padding: '16px 24px', marginBottom: '24px', border: '1.5px solid #B5D4F4' }}>
                        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Total Items</div>
                                <div style={{ fontSize: '22px', fontWeight: '800', color: colors.deepBlue }}>{orderItems.length}</div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Total Qty</div>
                                <div style={{ fontSize: '22px', fontWeight: '800', color: colors.orange }}>
                                    {orderItems.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0)}
                                </div>
                            </div>
                            <div>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>Customer</div>
                                <div style={{ fontSize: '15px', fontWeight: '700', color: colors.blue }}>{customerInfo.name || '—'}</div>
                            </div>
                        </div>
                        <button type="submit" disabled={submitting}
                            style={{ background: submitting ? '#aaa' : colors.deepBlue, color: '#fff', border: 'none', padding: '14px 40px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', cursor: submitting ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '10px', transition: 'background 0.2s', whiteSpace: 'nowrap' }}
                            onMouseOver={e => { if (!submitting) e.currentTarget.style.background = colors.orange; }}
                            onMouseOut={e => { if (!submitting) e.currentTarget.style.background = colors.deepBlue; }}
                        >
                            {submitting
                                ? <><i className="ph ph-spinner" /> Submitting...</>
                                : <><i className="ph-fill ph-paper-plane-tilt" /> Submit Bulk Order</>
                            }
                        </button>
                    </div>

                </form>
            </div>
        </section>
    );
};

export default BulkOrderForm;